import React, { useState, useRef, useCallback, useEffect } from 'react';
import PaneTaskList from './PaneTaskList';
import CompletedTaskList from './CompletedTaskList';
import ContextMenu from './ContextMenu';
import { Droppable } from './droppable';
import Task from './common/Task';
import { GET_API } from '../api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const TaskPane = ({
    tasks,
    setTasks, // Add this prop
    isAdding,
    values,
    handleChange,
    handleSubmit,
    toggleAdding,
    textareaRef,
    handleKeyDown,
    handleDelete,
    handleComplete,
    handleUncomplete,
    isLoading,
    fetchCompletedTasks,
    completedTasks
}) => {
    const [activeTab, setActiveTab] = useState('tasks');
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, taskId: null });
    const paneRef = useRef(null);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        if (tab === 'completed') {
            fetchCompletedTasks();
        }
    }, [fetchCompletedTasks]);

    const handleContextMenu = useCallback((e, taskId) => {
        e.preventDefault();
        setContextMenu({ visible: true, x: e.clientX, y: e.clientY, taskId });
    }, []);

    const handleClickOutside = useCallback((e) => {
        if (paneRef.current && !paneRef.current.contains(e.target)) {
            setContextMenu({ visible: false, x: 0, y: 0, taskId: null });
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        console.log('completedTasks updated:', completedTasks);
    }, [completedTasks]);

    const renderTasks = (taskList) => {
        console.log('Rendering tasks:', taskList);
        return (
            <Droppable id={activeTab === 'tasks' ? "pane" : "completed-pane"}>
                <div className='overflow-y-auto flex-grow max-h-[calc(100vh-300px)]'>
                    {isLoading ? (
                        <p>Loading tasks...</p>
                    ) : taskList && taskList.length > 0 ? (
                        taskList.map((task) => (
                            <Task
                                key={task.task_id}
                                task={task}
                                onContextMenu={handleContextMenu}
                                onDelete={() => handleDelete(task.task_id, "pane")} //TODO: change to activeTab
                                onComplete={handleComplete}
                                isDraggable={true}
                            />
                        ))
                    ) : (
                        <p>No tasks available.</p>
                    )}
                </div>
            </Droppable>
        );
    };

    const onCompleteTask = useCallback((taskId) => {
        if (typeof handleComplete === 'function') {
            handleComplete(taskId);
        } else {
            console.warn('handleComplete is not defined');
        }
        setContextMenu({ visible: false, x: 0, y: 0, taskId: null });
    }, [handleComplete]);

    const onUncompleteTask = useCallback((taskId) => {
        if (typeof handleUncomplete === 'function') {
            handleUncomplete(taskId);
        } else {
            console.warn('handleUncomplete is not defined');
        }
        setContextMenu({ visible: false, x: 0, y: 0, taskId: null });
    }, [handleUncomplete]);

    const onTaskComplete = useCallback((taskId) => {
        handleComplete(taskId);
        setTasks(prevTasks => prevTasks.filter(task => task.task_id !== taskId));
        fetchCompletedTasks(); // Refresh completed tasks
    }, [handleComplete, setTasks, fetchCompletedTasks]);

    return (
        <div ref={paneRef} className='w-64 flex flex-col ml-4 bg-gray-100 p-4 rounded-[14px] bg-orange-100 shadow-lg'>
            <div className="flex mb-4">
                <button
                    className={`flex-1 py-2 px-4 ${activeTab === 'tasks' ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'} rounded-l-[14px] transition-colors duration-200`}
                    onClick={() => handleTabChange('tasks')}
                >
                    Tasks
                </button>
                <button
                    className={`flex-1 py-2 px-4 ${activeTab === 'completed' ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'} rounded-r-[14px] transition-colors duration-200`}
                    onClick={() => handleTabChange('completed')}
                >
                    Completed
                </button>
            </div>

            {activeTab === 'tasks' ? (
                <PaneTaskList
                    tasks={tasks}
                    isLoading={isLoading}
                    handleContextMenu={handleContextMenu}
                    handleDelete={handleDelete}
                    onComplete={onTaskComplete}
                />
            ) : (
                <CompletedTaskList
                    completedTasks={completedTasks}
                    isLoading={isLoading}
                    handleContextMenu={handleContextMenu}
                    handleDelete={handleDelete}
                />
            )}

            {activeTab === 'tasks' && (
                isAdding ? (
                    <form onSubmit={handleSubmit} className='mt-4'>
                        <textarea
                            ref={textareaRef}
                            name='name'
                            value={values.name}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder='Task name'
                            className='w-full p-2 border border-gray-300 rounded-[14px] mb-2 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                            rows={3}
                        />
                        <div className='flex justify-between'>
                            <button
                                type='submit'
                                className='bg-blue-500 text-white py-2 px-4 rounded-[14px] hover:bg-blue-600 transition-colors duration-200'
                            >
                                Add
                            </button>
                            <button
                                type='button'
                                onClick={toggleAdding}
                                className='bg-gray-300 text-gray-700 py-2 px-4 rounded-[14px] hover:bg-gray-400 transition-colors duration-200'
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <button
                        onClick={toggleAdding}
                        className='bg-blue-500 text-white py-2 px-4 rounded-[14px] mt-4 hover:bg-blue-600 transition-colors duration-200 w-full'
                    >
                        Add Task
                    </button>
                )
            )}

            {contextMenu.visible && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onComplete={onCompleteTask}
                    onUncomplete={onUncompleteTask}
                    onClose={() => setContextMenu({ visible: false, x: 0, y: 0, taskId: null })}
                    isCompleted={activeTab === 'completed'}
                    taskId={contextMenu.taskId}
                />
            )}
        </div>
    );
};

export default TaskPane;